<template>
  <div>
    <h5 class="mb-4"><strong>Select the award</strong></h5>

    <div v-for="(section, index) in sections" :key="section.label">
      <div class="section-label" :class="{ 'mt-4': index > 0 }">{{ section.label }}</div>
      <div
        v-for="program in section.programs"
        :key="program.recognition_award_program_id"
        class="selectable-row"
        :class="{ selected: isSelected(program) }"
        role="button"
        @click="$emit('select', program)"
      >
        <div class="selectable-row__left">
          <div class="selectable-row__badge">
            <img
              v-if="program.badge_presigned_url"
              :src="program.badge_presigned_url"
              :alt="program.program_name"
              class="selectable-row__badge-image"
            />
            <font-awesome-icon v-else :icon="['fad', 'award']" class="selectable-row__badge-icon" />
          </div>
          <div class="selectable-row__text">
            <span class="selectable-row__name">{{ program.program_name }}</span>
            <span
              v-if="program.properties?.short_description"
              v-b-tooltip.hover="program.properties.short_description"
              class="selectable-row__subtitle"
            >
              {{ program.properties.short_description }}
            </span>
          </div>
        </div>
        <div class="selectable-row__indicator"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { RECOGNITION_AWARD_TYPES } from '@/pages/recognition/constants';

const props = defineProps({
  programs: {
    type: Array,
    default: () => [],
  },
  selectedProgram: {
    type: Object,
    default: null,
  },
});

defineEmits(['select']);

const activeOfType = programType => props.programs.filter(p => p.program_type === programType && p.is_active);

const sections = computed(() =>
  [
    { label: 'Send free form recognition', programs: activeOfType(RECOGNITION_AWARD_TYPES.OPEN) },
    { label: 'Give a company award', programs: activeOfType(RECOGNITION_AWARD_TYPES.PROGRAM) },
  ].filter(section => section.programs.length > 0),
);

function isSelected(program) {
  return props.selectedProgram?.recognition_award_program_id === program.recognition_award_program_id;
}
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/give-recognition';

.selectable-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 0.5rem;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  margin-bottom: 0.5rem;

  &:hover {
    border-color: var(--palette-color-core-1-lighten-30);
  }

  &.selected {
    border-color: var(--palette-color-core-1-lighten-30);
    box-shadow: 0 0 0 2px var(--palette-color-core-1-lighten-50);
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__badge {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background-color: var(--palette-color-core-1-lighten-80);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
  }

  &__badge-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__badge-icon {
    color: var(--palette-color-brand-primary);
    font-size: 1.25rem;
  }

  &__name {
    font-weight: 500;
  }

  &__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__subtitle {
    font-size: 0.75rem;
    color: var(--palette-color-default-lighten-30);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  &__indicator {
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    border: 2px solid var(--palette-color-default-lighten-90);
    background: var(--palette-color-base-white);
    flex-shrink: 0;
  }

  &.selected &__indicator {
    border-color: var(--palette-color-brand-primary);
    background: var(--palette-color-brand-primary);
    box-shadow: inset 0 0 0 4px var(--palette-color-base-white);
  }
}
</style>
